
import { Component, Vue, Prop, Watch, Model } from "vue-property-decorator"
import vSelect from "vue-select"
import { Getter } from "vuex-class"
import { validationMixin } from "vuelidate"
import { required, maxLength, email } from "vuelidate/lib/validators"
import { filter } from "lodash"
import api from "@/services/api"
import { CaseEmailContact, CaseDetails } from "@/types"
import { TenantFeatures } from "@/types/tenantFeatures"
import AttachPrintTemplate from "@/components/CaseDetails/AttachPrintTemplate.vue"

import { Format } from "@/types/format"
import { RenderType } from "@/types/renderTypes"

const MaxNoteLength = 5000
const AutoHideDelay = 10000
const ToastTitle = "Send Email"

@Component({
	components: {
		"vue-select": vSelect,
		"attach-print-template": AttachPrintTemplate
	},
	mixins: [validationMixin],
	validations: {
		note: {
			maxLength: maxLength(MaxNoteLength)
		},
		addresses: {
			required,
			$each: {
				email: { email }
			}
		}
	}
})
export default class SendEmailModal extends Vue {
	@Getter isFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Prop() currentCase!: CaseDetails;
	@Model("change", { type: Boolean, default: false }) readonly isVisible!: boolean;

	private emails: CaseEmailContact[] = [];
	private showOverlay: boolean = false;

	private addresses: CaseEmailContact[] = [];
	private note: string = "";
	private includeAttachments: boolean = true;

	private format: Format | null = null;
	private renderType: RenderType | null = null;

	private async mounted (): Promise<void> {
		await this.loadContacts()
	}

	private get isSendEmailEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.SendEmail)
	}

	private get isprintTemplatesAttachToEmailEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.PdfTemplatesAttachToEmail)
	}

	@Watch("currentCase")
	private async loadContacts (): Promise<void> {
		this.showOverlay = true
		this.emails = await api.getContactsForCase(this.currentCase.id!)
		this.showOverlay = false
	}

	private createOption (newOption: string): CaseEmailContact {
		const newContact = {
			email: newOption,
			fullName: null
		}

		this.$emit("option:created", newContact)
		return newContact
	}

	private getOptionLabel (option: CaseEmailContact): string {
		if (option.fullName) {
			return option.fullName
		}
		return option.email
	}

	private get noteInvalidFeedback (): string {
		if (!this.$v.note.maxLength) {
			return `Note must have no more than ${this.$v.note.$params.maxLength.max} letters.`
		}
		return ""
	}

	private get addressesInvalidFeedback (): string {
		const { required, $each: each } = this.$v.addresses
		if (!required) {
			return "Please specify at least one recipient"
		}

		const invalidEmails = filter(each!.$iter, v => !v!.email!.email).map(v => v!.email!.$model)
		if (invalidEmails.length > 0) {
			const adrWord = invalidEmails.length === 1 ? "address" : "addresses"
			const addresses = invalidEmails.map(a => `"${a}"`).join(", ")
			return `The ${adrWord} ${addresses} in the "Email Address" field was not recognized. Please make sure that all addresses are properly formed.`
		}

		return ""
	}

	private get noteValidFeedback (): string {
		const charactersUsed = this.note !== null ? this.note.length : 0
		return `Characters used: ${charactersUsed}/${MaxNoteLength}`
	}

	private get printTemplateValidation (): boolean {
		if (!this.isprintTemplatesAttachToEmailEnabled) {
			return true
		}

		if (this.format != null && this.renderType == null) {
			return false
		}

		return true
	}

	private onHidden (): void {
		this.addresses = []
		this.note = ""
		this.includeAttachments = true
		this.$v.$reset()
	}

	private onModalVisibilityChange (isVisible: boolean) {
		this.$emit("change", isVisible)
	}

	private async onSend (vModalEvent: any): Promise<void> {
		if (vModalEvent != null) {
			vModalEvent.preventDefault()
		}

		const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions()

		const payload = {
			contacts: [...this.addresses],
			note: this.note,
			includeAttachments: this.includeAttachments,
			formatId: this.format?.id,
			FormatRenderTypeId: this.renderType?.id,
			timeZone,
			locale
		}

		this.showOverlay = true
		// send email
		await api.sendCaseEmail(this.currentCase.id!, payload).then(this.onSuccess, this.onError)
		this.showOverlay = false
	}

	private onSuccess (): void {
		// show the success message to user
		const recipients = this.addresses.map(a => `"${a.email}"`).join(", ")
		const message = `Email sent to recipients: \r\n ${recipients}`
		this.showMessage(message, "success")

		// close the modal using the id attribute
		this.$nextTick(() => {
			this.$bvModal.hide("send-email-modal")
		})
	}

	private onError (err: any): void {
		this.showMessage(err, "danger")
	}

	private showMessage (message: string, variant: string): void	{
		this.$bvToast.toast(message, {
			title: ToastTitle,
			variant: variant,
			solid: true,
			autoHideDelay: AutoHideDelay
		})
	}

	private onTemplateSettingsSet (selectedFormat: Format, selectedRenderType: RenderType) {
		this.format = selectedFormat
		this.renderType = selectedRenderType
	}
}
